/** **Duplicate…** (plan §3 "Actions", D5, O13): a one-step confirmation on the Actions button —
 *  what the copy is called and what is copied (every field, access tags and tags, nearby
 *  activities, ratings, and copies of its gallery images and documents) — then the 202's
 *  background task, followed in place through the bgtasks infrastructure (`TaskProgressCard`:
 *  state, progress, the error, the logs, a link to the task). When it completes, its result
 *  names the copy: what was copied, any warnings, and **Open the copy**. A failed task says
 *  nothing was created.
 *
 *  The draft is not copied: with unsaved changes the confirmation says the copy takes the saved
 *  version.
 *
 *  Closing the popover loses nothing, nor does leaving the page: the run is held outside any
 *  page (`duplicateRuns`), and the app shell's `DuplicateRunsHost` announces its end in a
 *  snackbar (with Open the copy) unless the popover already showed it. Reopened while the copy
 *  runs — after coming back too — the popover follows it again. A second start while one runs
 *  is the server's 409 `duplicate_in_progress`: the popover follows that task instead.
 *
 *  Offered to `console_activities_manage` on an activity with an operator; not gated by the
 *  catalog edit switch (plan A10b-1: duplicate stays outside it). */
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Alert, Box, Button, LinearProgress, Popover, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';

import type { ActivityDetail } from '@/api/activities';
import {
  actionError,
  duplicateActivity,
  fetchDuplicateResult,
  type ActivityDuplicateAccepted,
  type ActivityDuplicateResult,
} from '@/api/activityActions';
import * as bgtasksApi from '@/api/bgtasks';
import { isActiveState, TASK_STATE, type BackgroundTaskDetail } from '@/api/bgtasks';
import { useSnackbar } from '@/components/Snackbar';
import { TaskProgressCard } from '@/features/bgtasks/TaskProgressCard';
import { ImmediateActionButton } from '@/features/records/actions/ImmediateActionButton';
import { Kbd } from '@/features/records/page/Kbd';

import { activityTitle } from '../parts/activityNames';

import {
  reportDuplicateRun,
  setDuplicateFollowing,
  startDuplicateRun,
  useDuplicateRun,
  type DuplicateRun,
} from './duplicateRuns';

export type { DuplicateRun } from './duplicateRuns';

const PAPER_SX = { p: 1.5, mt: 0.5, width: 380, maxWidth: 'calc(100vw - 32px)', display: 'grid', gap: 1 } as const;
const DETAIL_POLL_MS = 5000;

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

/** The copy's default name — the server's: the internal name with " (copy)". */
export function copyName(a: Pick<ActivityDetail, 'name' | 'id'>): string {
  return `${a.name?.trim() || `Activity #${a.id}`} (copy)`;
}

/** What the copy takes, in a sentence. */
export function duplicateCopiesText(a: Pick<ActivityDetail, 'image_count' | 'document_count'>): string {
  const files = [
    a.image_count ? plural(a.image_count, 'gallery image') : null,
    a.document_count ? plural(a.document_count, 'document') : null,
  ].filter(Boolean);
  const copies = files.length ? `, and copies of its ${files.join(' and ')}` : '';
  return `Every field, the access tags and tags, the nearby activities and the ratings${copies}.`;
}

/** What a finished copy holds: "104 of 104 images and 2 of 2 documents copied." */
export function duplicateResultText(r: Pick<ActivityDuplicateResult, 'images' | 'documents'>): string {
  const part = (c: { source: number; copied: number }, noun: string) =>
    c.source === 0 ? null : `${c.copied.toLocaleString('en-US')} of ${plural(c.source, noun)}`;
  const parts = [part(r.images, 'image'), part(r.documents, 'document')].filter(Boolean);
  return parts.length ? `${parts.join(' and ')} copied.` : 'Nothing to copy beyond its fields.';
}

/** The draft stays out of the copy: said when there is one. */
export function duplicateDraftText(unsaved: number): string {
  return `Copies the saved version: your ${plural(unsaved, 'unsaved change')} ${unsaved === 1 ? 'is' : 'are'} not in the copy.`;
}

/** Why the duplicate was not started, in the operator's terms. */
export function duplicateRefusalText(e: unknown): string {
  const a = actionError(e);
  if (a?.code === 'duplicate_in_progress') return 'A copy of this activity is already being made.';
  if (a?.detail) return a.detail;
  return e instanceof Error && e.message ? e.message : 'The copy could not be started.';
}

/** How a duplicate run ended, once it has: its task, and the result of a completed one. */
export function useDuplicateOutcome(taskId: string | null) {
  // The task details dialog's key and cadence: co-mounted monitors share one request.
  const taskQ = useQuery({
    queryKey: ['bgtask', taskId],
    queryFn: () => bgtasksApi.retrieve(taskId!),
    enabled: taskId !== null,
    refetchInterval: (q) => (q.state.data && isActiveState(q.state.data.state) ? DETAIL_POLL_MS : false),
  });
  const task: BackgroundTaskDetail | undefined = taskQ.data;
  const completed = task?.state === TASK_STATE.COMPLETED;
  const resultQ = useQuery({
    queryKey: ['bgtask', taskId, 'result'],
    queryFn: () => fetchDuplicateResult(taskId!),
    enabled: taskId !== null && completed,
    staleTime: Infinity,
  });
  const ended = task !== undefined && !isActiveState(task.state);
  return { task, ended, completed, result: resultQ.data ?? null, resultFailed: Boolean(resultQ.error) };
}

/** The copy's console page: its result's `path`, else the task's `metrics.activity_id`. */
export function copyPath(result: ActivityDuplicateResult | null, task: BackgroundTaskDetail | undefined): string | null {
  if (result?.path) return result.path;
  const id = task?.metrics?.activity_id;
  return typeof id === 'number' ? `/activities/${id}` : null;
}

export function DuplicatePopover({
  detail,
  anchorEl,
  onClose,
  unsavedChanges = 0,
}: {
  detail: ActivityDetail;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** The page's draft holds this many changes — not in the copy. */
  unsavedChanges?: number;
}) {
  const titleId = useId();
  const id = detail.id;
  const start = useMutation({ mutationFn: () => duplicateActivity(id) });
  // The run of this activity's copy, wherever it was started from (`duplicateRuns`).
  const run = useDuplicateRun(id);
  const following = run && !run.reported ? run : null;
  const [shown, setShown] = useState<DuplicateRun | null>(null);
  // The run on screen: the one in flight, else the one this popover just showed end.
  const current = following ?? shown;
  const outcome = useDuplicateOutcome(current?.taskId ?? null);
  const open = Boolean(anchorEl);

  // Open, the popover tells the run's end; closed (or gone with the page), the app shell does.
  const runTask = run?.taskId;
  useEffect(() => {
    if (!open || !runTask) return undefined;
    setDuplicateFollowing(id, true);
    return () => setDuplicateFollowing(id, false);
  }, [open, id, runTask]);

  // Shown ending while open: nobody need announce it again.
  useEffect(() => {
    if (open && following && outcome.ended) {
      setShown(following);
      reportDuplicateRun(id, following.taskId);
    }
  }, [open, following, outcome.ended, id]);

  // A fresh opening without a run in flight starts at the confirmation.
  useEffect(() => {
    if (!open) {
      setShown(null);
      start.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const title = activityTitle(detail);
  const send = () =>
    start.mutate(undefined, {
      onSuccess: (r: ActivityDuplicateAccepted) =>
        startDuplicateRun(id, { taskId: r.task_id, name: r.name, sourceTitle: title, following: true }),
      onError: (e) => {
        // A double start: follow the copy already running.
        const a = actionError(e);
        if (a?.code === 'duplicate_in_progress' && a.task_id) {
          startDuplicateRun(id, { taskId: a.task_id, name: copyName(detail), sourceTitle: title, following: true });
        }
      },
    });

  const refused = start.isError && !current ? duplicateRefusalText(start.error) : null;
  const sending = start.isPending;
  const path = copyPath(outcome.result, outcome.task);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={() => {
        if (!sending) onClose();
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          role: current ? 'dialog' : 'alertdialog',
          'aria-labelledby': titleId,
          'aria-busy': sending || undefined,
          'data-duplicate': current ? (outcome.ended ? 'ended' : 'running') : refused ? 'refused' : 'confirm',
          sx: PAPER_SX,
        } as object,
      }}
    >
      <Typography id={titleId} variant="body2" sx={{ fontWeight: 600 }}>
        Duplicate “{title}”
      </Typography>

      {!current && !refused && (
        <>
          <Typography variant="body2" component="p" sx={{ m: 0 }}>
            Makes a new activity of this operator, “{copyName(detail)}”: {duplicateCopiesText(detail)}
          </Typography>
          {unsavedChanges > 0 && (
            <Typography
              variant="body2"
              component="p"
              sx={{ m: 0, display: 'flex', gap: 0.75, alignItems: 'flex-start' }}
              data-duplicate-draft
            >
              <EditNoteOutlinedIcon aria-hidden sx={{ fontSize: 16, mt: '2px', flex: 'none', color: 'text.secondary' }} />
              <span>{duplicateDraftText(unsavedChanges)}</span>
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0 }}>
            A background task · nothing is created if it fails · audited.
          </Typography>
          {sending && <LinearProgress aria-label="Starting the copy" sx={{ height: 2 }} />}
          <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={onClose} disabled={sending}>
              Cancel
            </Button>
            <ImmediateActionButton variant="contained" autoFocus onClick={send} disabled={sending} data-confirm-duplicate>
              {sending ? 'Starting…' : 'Duplicate now'}
              {!sending && <Kbd>↵</Kbd>}
            </ImmediateActionButton>
          </Box>
        </>
      )}

      {refused && (
        <>
          <Alert severity="warning" sx={{ py: 0, '& .MuiAlert-message': { typography: 'body2', py: 0.75 } }}>
            Nothing was copied: {refused}
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="small" onClick={onClose} autoFocus>
              Close
            </Button>
          </Box>
        </>
      )}

      {current && (
        <>
          <DuplicateStatus run={current} outcome={outcome} />
          <TaskProgressCard taskId={current.taskId} dense />
          <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={onClose} autoFocus={!path}>
              Close
            </Button>
            {outcome.completed && path && (
              <Button size="small" variant="contained" component={RouterLink} to={path} onClick={onClose} autoFocus>
                Open the copy
              </Button>
            )}
          </Box>
        </>
      )}
    </Popover>
  );
}

function DuplicateStatus({ run, outcome }: { run: DuplicateRun; outcome: ReturnType<typeof useDuplicateOutcome> }) {
  const { task, ended, completed, result } = outcome;
  if (!ended) {
    return (
      <Typography variant="body2" component="p" sx={{ m: 0 }} role="status">
        Copying into “{run.name}”. You can close this — the copy is announced when it is ready.
      </Typography>
    );
  }
  if (!completed) {
    return (
      <Typography variant="body2" component="p" sx={{ m: 0 }} role="status">
        {task?.state === TASK_STATE.CANCELLED
          ? 'The copy was cancelled — nothing was created.'
          : 'The copy failed — nothing was created.'}
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'grid', gap: 0.5 }} role="status">
      <Typography variant="body2" component="p" sx={{ m: 0 }}>
        “{result?.name ?? run.name}” is ready.{result ? ` ${duplicateResultText(result)}` : ''}
      </Typography>
      {result?.warnings.map((w) => (
        <Typography
          key={w}
          variant="body2"
          component="p"
          sx={{ m: 0, display: 'flex', gap: 0.75, alignItems: 'flex-start' }}
          data-duplicate-warning
        >
          <WarningAmberIcon aria-hidden sx={{ fontSize: 16, mt: '2px', flex: 'none', color: 'warning.main' }} />
          <span>{w}</span>
        </Typography>
      ))}
    </Box>
  );
}

/** A run no popover shows: its end, announced once in a snackbar — the copy of `sourceTitle`
 *  with **Open the copy**, or that nothing was created. Mounted by the app shell
 *  (`DuplicateRunsHost`), so it reaches the operator on any page. */
export function DuplicateWatcher({
  run,
  sourceTitle,
  onReported,
}: {
  run: DuplicateRun;
  sourceTitle: string;
  onReported: () => void;
}) {
  const outcome = useDuplicateOutcome(run.taskId);
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const { task, ended, completed, result, resultFailed } = outcome;
  const path = copyPath(result, task);
  const ready = ended && (!completed || result !== null || resultFailed);
  const done = useRef(false);
  useEffect(() => {
    if (!ready || done.current) return;
    done.current = true;
    if (completed) {
      snackbar.show(`“${result?.name ?? run.name}” is ready — the copy of “${sourceTitle}”.`, 'success', {
        autoHideDuration: 20000,
        ...(path ? { action: { label: 'Open the copy', onClick: () => navigate(path) } } : {}),
      });
    } else {
      snackbar.show(
        task?.state === TASK_STATE.CANCELLED
          ? `The copy of “${sourceTitle}” was cancelled — nothing was created.`
          : `The copy of “${sourceTitle}” failed — nothing was created.${task?.error_message ? ` ${task.error_message}` : ''}`,
        'error',
      );
    }
    onReported();
  }, [ready, completed, result, run.name, sourceTitle, path, task, snackbar, navigate, onReported]);
  return null;
}
